/** Small shared pieces for the build panels: data access, commits, item cells, money. */
import type { ComponentChildren, JSX } from 'preact';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import type { Game } from '../../game/Game';
import { type EquipItem, type ItemDef, ITEMS, type Rarity } from '../../game/items';
import type { Progression } from '../../game/Progression';
import { registry } from '../../sim/registry';
import type { ClassDef } from '../../sim/registry';
import { api } from '../../ui/app/api';
import { bump, ui } from '../../ui/app/store';
import { itemIcon } from './art';
import { ItemTooltip, type ItemTooltipProps, Money, tip } from './Tooltip';

let game: Game | null = null;

/** Called by install(): lets panels request a debounced save after important changes. */
export function bindGame(g: Game): void {
  game = g;
}

export interface Ctx {
  p: Progression;
  c: CharacterSave;
  a: AccountSave;
  cls: ClassDef | undefined;
}

function currentProgression(): Progression | null {
  try {
    return api().progression;
  } catch {
    return null;
  }
}

/** Current progression context; subscribes the calling component to `ui.version`. */
export function useCtx(): Ctx | null {
  void ui.version.value;
  const p = currentProgression();
  if (!p) return null;
  return { p, c: p.char, a: p.account, cls: registry.classes.get(p.char.classId) };
}

/**
 * Finishes a mutation: rebuilds the player when stats or skills changed, re-renders
 * panels and schedules a save.
 */
export function commit(opts: { stats?: boolean; save?: boolean } = {}): void {
  if (opts.stats) {
    try {
      api().refreshPlayer();
    } catch {
      /* no game running (tests / previews) */
    }
  }
  bump();
  if (opts.save !== false && game) game.store.saveSoon(game.account);
}

export function notify(text: string, color?: string): void {
  try {
    api().notify(text, color);
  } catch {
    /* ignore */
  }
}

/** Asks for confirmation through the shared confirm dialog. */
export function confirmThen(text: string, okLabel: string, ok: () => void): void {
  ui.confirm.value = { text, okLabel, ok };
}

export { Money };

// ---------------------------------------------------------------- item cell

export interface ItemCellProps extends Omit<JSX.HTMLAttributes<HTMLButtonElement>, 'size' | 'icon'> {
  item?: EquipItem | ItemDef | string | null;
  count?: number;
  size?: number;
  selected?: boolean;
  /** Owner's weapon kind for weapon icons. */
  weapon?: string;
  /** Tooltip options; pass `false` to disable. */
  tipProps?: Omit<ItemTooltipProps, 'item'> | false;
  /** Small corner badge (e.g. upgrade arrow). */
  badge?: ComponentChildren;
  /** Placeholder art for empty cells. */
  ghost?: string;
  label?: string;
}

function rarityOf(item: EquipItem | ItemDef | string): Rarity {
  if (typeof item === 'string') return 'normal';
  return item.rarity;
}

/** A square item slot: rarity gradient, icon, stack count, +N badge and tooltip. */
export function ItemCell({ item, count, size = 46, selected, weapon, tipProps, badge, ghost, label, class: cls, ...rest }: ItemCellProps) {
  const style = { width: `${size}px`, height: `${size}px` };
  if (!item) {
    return (
      <button type="button" class={`bcell bcell--empty ${selected ? 'is-sel' : ''} ${typeof cls === 'string' ? cls : ''}`} style={style} aria-label={label ?? 'Empty slot'} {...rest}>
        {ghost && <img class="bcell-ghost" src={ghost} alt="" draggable={false} />}
      </button>
    );
  }
  const rarity = typeof item === 'string' ? rarityFromId(item) : rarityOf(item);
  const plus = typeof item !== 'string' && 'uid' in item && item.kind === 'gear' && item.plus > 0 ? item.plus : 0;
  const name = typeof item === 'string' ? item : item.name;
  const tipHandlers = tipProps === false ? {} : tip(() => <ItemTooltip item={item} {...(tipProps ?? {})} />);
  return (
    <button type="button" class={`bcell rbg-${rarity} ${selected ? 'is-sel' : ''} ${typeof cls === 'string' ? cls : ''}`} style={style} aria-label={label ?? name} {...tipHandlers} {...rest}>
      <img src={itemIcon(item, weapon)} alt="" draggable={false} />
      {plus > 0 && <span class="bcell-plus">+{plus}</span>}
      {count !== undefined && count > 1 && <span class="bcell-n">{count > 9999 ? `${Math.floor(count / 1000)}k` : count}</span>}
      {badge}
    </button>
  );
}

const rarityFromId = (id: string): Rarity => ITEMS[id]?.rarity ?? 'normal';

/** Non-interactive item icon in a rarity cell (for use inside other buttons/rows). */
export function ItemIcon({ item, size = 40, weapon }: { item: EquipItem | ItemDef | string; size?: number; weapon?: string }) {
  const rarity = typeof item === 'string' ? rarityFromId(item) : rarityOf(item);
  const plus = typeof item !== 'string' && 'uid' in item && item.kind === 'gear' && item.plus > 0 ? item.plus : 0;
  return (
    <span class={`bcell bcell--static rbg-${rarity}`} style={{ width: `${size}px`, height: `${size}px` }} aria-hidden="true">
      <img src={itemIcon(item, weapon)} alt="" draggable={false} />
      {plus > 0 && <span class="bcell-plus">+{plus}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- misc

/** Section heading with the project's gold rule. */
export function Section({ title, right, children, class: cls = '' }: { title: string; right?: ComponentChildren; children?: ComponentChildren; class?: string }) {
  return (
    <section class={`bsec ${cls}`}>
      <div class="bsec-h">
        <span class="section-h">{title}</span>
        {right && <span class="bsec-r">{right}</span>}
      </div>
      {children}
    </section>
  );
}

/** Level pips (e.g. sigil levels 1–3, card awakening 0–5). */
export function Pips({ n, max, class: cls = '' }: { n: number; max: number; class?: string }) {
  return (
    <span class={`bdots ${cls}`} aria-label={`${n} of ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <i key={i} class={i < n ? 'on' : ''} />
      ))}
    </span>
  );
}

export const hueCss = (h: number): string => `#${(h & 0xffffff).toString(16).padStart(6, '0')}`;
